import type { Check, Control, Text } from '@eformulieren/model'
import { date, formatNumber, formatList, perLanguage } from './language.js'

/**
 * De standaardmelding per regelsoort, in beide talen (ontwerp 5.7, R-30, D-16).
 *
 * Elke regel heeft een eigen, in gewone taal geformuleerde melding die de
 * inrichter kan aanpassen. De engine levert de standaard, de inrichter
 * overschrijft hem per regel waar die niet past. De melding reist in beide talen
 * mee in de evaluatiestatus, zodat een taalwissel van de invuller (R-144) geen
 * nieuwe evaluatie vraagt en de client voor server-side geëvalueerde regels
 * niets van de regel zelf hoeft te weten.
 *
 * Drie keuzes die het uitleggen waard zijn.
 *
 * **Bij `verplicht` noemt de melding het veld, als de aanroeper het meegeeft.**
 * Tot de audit van 30 oktober 2026 noemde geen enkele standaardmelding het veld
 * ("Dit veld is verplicht."). Dat is een generieke tekst, en in het overzicht van
 * open punten en voor een schermlezer die de melding los voorleest zegt hij niet
 * waar het om gaat (audit R-27, ontwerp 5.7). Nu zegt hij het met het label tussen
 * aanhalingstekens en een werkwoord dat bij de control past: vul in, kies, voeg toe.
 * Tussen aanhalingstekens, omdat een label een eigennaam, een zin of een vraag kan
 * zijn en niet zonder meer met een kleine letter in een zin past. Zonder veld blijft
 * het de algemene zin; de andere soorten noemen een grens en geen veld.
 *
 * **Een zichtbaarheidsregel heeft geen melding.** Zichtbaarheid is geen controle
 * (ontwerp 5.3): er valt niets te melden, het veld staat er of het staat er niet.
 * Daarom neemt deze functie een `Controle` en niet een `Regel`; de typecheck
 * houdt dat vast.
 *
 * **De regex-expressie staat niet in de melding.** Wie de regel mag vastleggen is
 * beheer (D-17); wie de melding leest is de invuller. Een expressie voorlezen aan
 * iemand die hem niet kan lezen helpt niet, dus daar hoort een eigen melding bij
 * die de inrichter zelf schrijft.
 *
 * **Bij `verplicht` volgt de melding het mechanisme.** Dat is de enige soort waar
 * dat gebeurt, en het is nodig omdat het woord "verplicht" een poort belooft. Staat
 * de regel op waarschuwing, dan blokkeert hij indienen niet (R-23, R-31) en zou
 * "dit veld is verplicht" de invuller iets onwaars vertellen; dan is het een duwtje
 * en luidt de melding zo. De aanduiding naast het label werkt dezelfde kant op:
 * `requiredFields` neemt alleen de harde regels (zie `velden.ts`). Bij de andere
 * soorten zegt de melding wat de grens is, en dat is waar of hij nu blokkeert of
 * adviseert.
 */
export function defaultNotice(check: Check, field?: NoticeField): Text {
  switch (check.soort) {
    case 'verplicht':
      // Bij een akkoord per persoon is verplicht minstens één akkoord dat nog geldt (eisenboek 12.56).
      if (field?.control === 'akkoordPerPersoon') {
        return perLanguage((language) =>
          language === 'nl'
            ? 'Indienen kan als er minstens één akkoord is dat nog over de inhoud van nu gaat.'
            : 'You can submit once there is at least one consent that covers the current content.',
        )
      }
      if (field?.label !== undefined) return requiredWithLabel(check.mechanisme, field.control, field.label)
      return check.mechanisme === 'hard'
        ? perLanguage((language) => (language === 'nl' ? 'Dit veld is verplicht.' : 'This field is required.'))
        : perLanguage((language) => (language === 'nl' ? 'Dit veld is nog leeg.' : 'This field is still empty.'))

    case 'maximumTekens':
      return perLanguage((language) =>
        language === 'nl'
          ? `Gebruik maximaal ${formatNumber(check.maximum, language)} tekens.`
          : `Use at most ${formatNumber(check.maximum, language)} characters.`,
      )

    case 'maximumWoorden':
      return perLanguage((language) =>
        language === 'nl'
          ? `Gebruik maximaal ${formatNumber(check.maximum, language)} woorden.`
          : `Use at most ${formatNumber(check.maximum, language)} words.`,
      )

    case 'minimumWoorden':
      return perLanguage((language) =>
        language === 'nl'
          ? `Gebruik minstens ${formatNumber(check.minimum, language)} woorden.`
          : `Use at least ${formatNumber(check.minimum, language)} words.`,
      )

    case 'getalsbereik': {
      const { minimum, maximum } = check
      return perLanguage((language) => {
        if (minimum !== undefined && maximum !== undefined) {
          return language === 'nl'
            ? `Vul een getal tussen ${formatNumber(minimum, language)} en ${formatNumber(maximum, language)} in.`
            : `Enter a number between ${formatNumber(minimum, language)} and ${formatNumber(maximum, language)}.`
        }
        if (minimum !== undefined) {
          return language === 'nl'
            ? `Vul een getal van ${formatNumber(minimum, language)} of hoger in.`
            : `Enter a number of ${formatNumber(minimum, language)} or higher.`
        }
        // Het schema dwingt af dat er een minimum, een maximum of beide staat,
        // dus dit is de tak met alleen een maximum.
        return language === 'nl'
          ? `Vul een getal van ${formatNumber(maximum ?? 0, language)} of lager in.`
          : `Enter a number of ${formatNumber(maximum ?? 0, language)} or lower.`
      })
    }

    case 'geheelGetal':
      return perLanguage((language) => (language === 'nl' ? 'Vul een geheel getal in.' : 'Enter a whole number.'))

    case 'datumbereik': {
      const { vroegste: earliest, laatste: last } = check
      return perLanguage((language) => {
        if (earliest !== undefined && last !== undefined) {
          return language === 'nl'
            ? `Kies een datum tussen ${date(earliest, language)} en ${date(last, language)}.`
            : `Choose a date between ${date(earliest, language)} and ${date(last, language)}.`
        }
        if (earliest !== undefined) {
          return language === 'nl'
            ? `Kies een datum op of na ${date(earliest, language)}.`
            : `Choose a date on or after ${date(earliest, language)}.`
        }
        return language === 'nl'
          ? `Kies een datum op of voor ${date(last ?? '', language)}.`
          : `Choose a date on or before ${date(last ?? '', language)}.`
      })
    }

    case 'identifierFormaat':
      return NOTICE_PER_IDENTIFIER[check.formaat]

    case 'akkoordJa':
      return perLanguage((language) =>
        language === 'nl'
          ? 'Vink dit akkoord aan om te kunnen indienen.'
          : 'Tick this consent before you can submit.',
      )

    case 'minimumAantal':
      return perLanguage((language) =>
        language === 'nl'
          ? `Geef er minstens ${formatNumber(check.minimum, language)} op.`
          : `Provide at least ${formatNumber(check.minimum, language)}.`,
      )

    case 'maximumAantal':
      return perLanguage((language) =>
        language === 'nl'
          ? `Geef er maximaal ${formatNumber(check.maximum, language)} op.`
          : `Provide at most ${formatNumber(check.maximum, language)}.`,
      )

    case 'bestandseisen': {
      const { typen: types, maximumAantal, maximumGrootteMb } = check
      const kinds = types.map((t) => t.toUpperCase())
      return perLanguage((language) => {
        const list = formatList(kinds, language)
        const size = `${formatNumber(maximumGrootteMb, language)} MB`
        if (language === 'nl') {
          const fileNl = maximumAantal === 1 ? 'bestand' : 'bestanden'
          return `Alleen ${list}, maximaal ${formatNumber(maximumAantal, language)} ${fileNl} van ${size}.`
        }
        const file = maximumAantal === 1 ? 'file' : 'files'
        return `Only ${list}, at most ${formatNumber(maximumAantal, language)} ${file} of ${size}.`
      })
    }

    case 'regexVorm':
      return perLanguage((language) =>
        language === 'nl'
          ? 'De invoer heeft niet de vorm die hier vereist is.'
          : 'The input does not have the format required here.',
      )

    case 'verbodenTermen': {
      const { termen: terms } = check
      return perLanguage((language) => {
        const list = formatList(
          terms.map((t) => `"${t}"`),
          language,
        )
        return language === 'nl'
          ? `Let op: vermijd hier ${list}.`
          : `Note: avoid ${list} here.`
      })
    }

    case 'korteSectie':
      return perLanguage((language) =>
        language === 'nl'
          ? `Deze sectie is korter dan ${formatNumber(check.drempelWoorden, language)} woorden. Controleer of hij compleet is.`
          : `This section is shorter than ${formatNumber(check.drempelWoorden, language)} words. Check whether it is complete.`,
      )

    default:
      return unknownKind(check)
  }
}

/**
 * De melding die de invuller te zien krijgt: die van de inrichter als hij er een
 * heeft ingevuld, anders de standaard (R-30, D-16).
 *
 * Dit is de enige plek waar die voorkeur wordt bepaald. Elk pad dat een bevinding
 * maakt gebruikt hem, zodat een overschreven melding nergens per ongeluk wordt
 * genegeerd.
 */
export function noticeFor(check: Check, field?: NoticeField): Text {
  return check.melding ?? defaultNotice(check, field)
}

/** Wat de melding van het doelveld nodig heeft: de control voor het werkwoord en het label. */
export interface NoticeField {
  readonly control: Control
  readonly label?: Text | undefined
}

/** Controls waar de invuller kiest in plaats van typt. */
const CHOOSE: ReadonlySet<Control> = new Set<Control>(['dropdown', 'radio', 'multiselect', 'checkbox', 'toggle'])
/** Controls waar de invuller iets toevoegt: een bestand. */
const ADD: ReadonlySet<Control> = new Set<Control>(['file', 'image'])

function requiredWithLabel(mechanism: 'hard' | 'waarschuwing', control: Control, label: Text): Text {
  return perLanguage((language) => {
    const name = label[language].trim()
    const nl = `„${name}”`
    const en = `"${name}"`
    if (mechanism !== 'hard') return language === 'nl' ? `${nl} is nog leeg.` : `${en} is still empty.`
    if (control === 'herhaalgroep') return language === 'nl' ? `Voeg een rij toe bij ${nl}.` : `Add a row to ${en}.`
    if (ADD.has(control)) return language === 'nl' ? `Voeg ${nl} toe.` : `Add ${en}.`
    if (CHOOSE.has(control)) return language === 'nl' ? `Kies een antwoord bij ${nl}.` : `Choose an answer for ${en}.`
    if (name.endsWith('?')) return language === 'nl' ? `Beantwoord de vraag ${nl}.` : `Answer the question ${en}.`
    return language === 'nl' ? `Vul ${nl} in.` : `Enter ${en}.`
  })
}

/** De identifiersoorten uit B11, uit het model gehaald en niet hier herhaald. */
export type IdentifierFormat = Extract<Check, { soort: 'identifierFormaat' }>['formaat']

/**
 * Per identifiersoort een eigen melding (R-27). De voorbeelden zijn vormen en
 * geen bestaande identifiers: een echte ORCID of DOI hoort bij een persoon of een
 * publicatie, en die staan hier niet in.
 *
 * Een `Record` over het type uit het model en niet een lijst hier: komt er een
 * soort bij in B11, dan valt de typecheck om in plaats van dat de melding stil
 * ontbreekt.
 */
export const NOTICE_PER_IDENTIFIER: Record<IdentifierFormat, Text> = {
  doi: {
    nl: 'Vul een DOI in de vorm 10.1234/abcd-5678 in.',
    en: 'Enter a DOI in the form 10.1234/abcd-5678.',
  },
  orcid: {
    nl: 'Vul een ORCID in de vorm 0000-0000-0000-0000 in.',
    en: 'Enter an ORCID in the form 0000-0000-0000-0000.',
  },
  url: {
    nl: 'Vul een volledig webadres in, bijvoorbeeld https://voorbeeld.nl.',
    en: 'Enter a complete web address, for example https://voorbeeld.nl.',
  },
  email: {
    nl: 'Vul een geldig e-mailadres in, bijvoorbeeld naam@voorbeeld.nl.',
    en: 'Enter a valid email address, for example name@voorbeeld.nl.',
  },
  telefoon: {
    nl: 'Vul een geldig telefoonnummer in, bijvoorbeeld +31 20 123 4567.',
    en: 'Enter a valid phone number, for example +31 20 123 4567.',
  },
}

/**
 * De vangnetregel: een soort erbij in B11 zonder melding erbij is een typefout in
 * plaats van een melding die stil ontbreekt.
 */
function unknownKind(check: never): never {
  throw new Error(`geen standaardmelding voor regelsoort ${JSON.stringify(check)}`)
}
